import {
  keepPreviousData,
  QueryFunctionContext,
  useInfiniteQuery,
  InfiniteData
} from "@tanstack/react-query";
import { getQueryKey } from "@key.ai/constants";
import eventClientApi from "@/api-client/event-client";
import { getCurrentEpoc } from "@/lib/utils";
import { ApiResponse } from "@/types/api";
import { cleanObject } from "@/lib/helper";

export const usePastEvents = ({ search }: { search: string }) => {
  const queryKey = getQueryKey("PAST_EVENTS_INVITES");

  const fetchPastEvents = async ({ pageParam = 1 }: { pageParam: number }) => {
    const { data } = await eventClientApi
      .get(`api/v1/events/participations`, {
        searchParams: cleanObject({
          page: pageParam,
          limit: 10,
          search: search
          //   date_operator: "lt",
          //   current_epoc: getCurrentEpoc()
        })
      })
      .json<any>();

    return { ...data, count: data?.count, pageParam };
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status, isLoading, refetch } =
    useInfiniteQuery({
      queryKey: [queryKey, search],
      queryFn: fetchPastEvents,
      getNextPageParam: (lastPage) => {
        // Check if more pages are available based on total count
        const totalPages = Math.ceil(lastPage?.count / 10);
        if (lastPage?.pageParam < totalPages) {
          return lastPage?.pageParam + 1;
        }
        return undefined; // Stop pagination
      },
      initialPageParam: 1,
      gcTime: 0,
      refetchOnMount: true,
      refetchInterval: false
    });
  const totalRecords = (data?.pages?.[0] && data?.pages?.[0]?.count) || 0;

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isLoading,
    totalRecords,
    refetch
  };
};

interface eventParticipation {
  id: string;
  title: string;
  timestamp: string;
  // Add more fields based on your actual API response
}

interface ApiResponsePagination<T> {
  data: T[];
  count?: number;
  pageParam?: number;
  totalRecords?: number;
}

export const useEventParticipations = ({
  eventId,
  search
}: {
  eventId: string;
  search?: string;
}) => {
  const queryKey = getQueryKey("PAST_EVENT_PARTICIPATIONS");

  const fetchEventParticipations = async ({ pageParam = 1 }: { pageParam: unknown }) => {
    const { data } = await eventClientApi
      .get(`api/v1/events/${eventId}/participations/info`, {
        searchParams: cleanObject({
          page: pageParam as number,
          limit: 10,
          search: search
        })
      })
      .json<ApiResponse<any>>();

    return {
      ...data,
      totalRecords: data?.count,
      pageParam
    };
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status, isLoading, refetch } =
    useInfiniteQuery<ApiResponsePagination<any>>({
      queryKey: [queryKey, search, eventId],
      queryFn: fetchEventParticipations,
      getNextPageParam: (lastPage) => {
        if (lastPage?.data?.length >= 10) return lastPage.pageParam! + 1;
        return undefined;
      },
      initialPageParam: 1,
      placeholderData: keepPreviousData,
      refetchOnMount: false,
      refetchInterval: false,
      enabled: !!eventId
    });
  const totalRecords = (data?.pages?.[0] && data?.pages?.[0]?.totalRecords) || 0;

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isLoading,
    refetch,
    totalRecords
  };
};
